import { createContext, useContext, useReducer, useRef, useCallback, useEffect, useSyncExternalStore, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/config/queryConfig'
import { isOpenCritical } from '@/lib/criticalEvents'
import { isSuspiciousAlarm } from '@/lib/suspiciousEvents'
import { useAuthStore } from '@/store/authStore'
import type { Event, PaginatedResponse } from '@/types'
import { parseEvent, type DetectionNotification, type NotificationVariant } from './shared'
import { PeekCards } from './PeekCards'
import { NotificationDrawer } from './NotificationDrawer'
import { seenEventIds } from './suspiciousToasts'

// ── Constants ───────────────────────────────────────────────────────────────

const MAX_PEEK = 4
/** #413 — the Suspicious toasts shown at once; more open ones are a "+ N more" chip (PeekCards). */
export const MAX_SUS_VISIBLE = 3
/** #413 (final review I4) — below 640 px one Suspicious toast at a time, so three never fill a phone's screen */
export const MAX_SUS_VISIBLE_PHONE = 1
const MAX_HISTORY = 50
const ALERT_DURATION = 8_000
const STANDARD_DURATION = 6_000
const HOVER_RESUME_DELAY = 3_000

// ── Context ─────────────────────────────────────────────────────────────────

interface NotificationContextValue {
  notifications: DetectionNotification[]
  peekIds: string[]
  /** #413 — notification ids of the open Suspicious toasts, newest first (no timer, apart from peekIds) */
  suspiciousIds: string[]
  exitingPeekIds: string[]
  unreadCount: number
  drawerOpen: boolean
  activeTab: 'all' | 'alerts' | 'motion'
  addNotification: (event: any) => void
  dismissPeek: (id: string) => void
  /** #413 — close one Suspicious toast in this tab; the event stays open */
  dismissSuspicious: (id: string) => void
  /** #413 — anyone's Seen: close the Suspicious toasts of these events */
  closeSuspiciousForEvents: (eventIds: number[]) => void
  dismissFromDrawer: (id: string) => void
  toggleDrawer: () => void
  markAllRead: () => void
  clearAll: () => void
  setActiveTab: (tab: 'all' | 'alerts' | 'motion') => void
  pausePeekTimer: (id: string) => void
  resumePeekTimer: (id: string) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider')
  return ctx
}

/**
 * #337 (fix round 1, review I-4) — is a critical event still open, by the open-critical list's page? true: in the
 * list and open. false: in the list but acknowledged (this tab acknowledges optimistically: the row stays, marked),
 * or missing from a page that holds every open one. undefined: no list yet, or missing from a page that does not
 * hold every open one (more than a page is open), which proves nothing.
 */
function stillOpen(page: PaginatedResponse<Event> | undefined, eventId: number): boolean | undefined {
  if (!page || !Array.isArray(page.results)) return undefined
  const row = page.results.find((e) => e.id === eventId)
  if (row) return isOpenCritical(row)
  return (page.count ?? page.results.length) <= page.results.length ? false : undefined
}

// ── Reducer ─────────────────────────────────────────────────────────────────

interface State {
  notifications: DetectionNotification[]
  peekIds: string[]
  suspiciousIds: string[]
  exitingPeekIds: string[]
  unreadCount: number
  drawerOpen: boolean
  activeTab: 'all' | 'alerts' | 'motion'
}

type Action =
  | { type: 'ADD'; notification: DetectionNotification; peek: boolean }
  | { type: 'EXIT_PEEK'; id: string }
  | { type: 'DISMISS_SUS'; id: string }
  | { type: 'CLOSE_SUS_EVENTS'; eventIds: number[] }
  | { type: 'SIGN_OUT' }
  | { type: 'DISMISS_PEEK'; id: string }
  | { type: 'DISMISS_FROM_DRAWER'; id: string }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'MARK_ALL_READ' }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_TAB'; tab: 'all' | 'alerts' | 'motion' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD': {
      // #413 (final review I1) — the history keeps MAX_HISTORY, plus any older notification whose Suspicious toast is
      // still open: that toast ends only on its close, anyone's Seen, sign-out or Clear all, never on 50 later events
      const open = new Set(state.suspiciousIds)
      const notifications = [action.notification, ...state.notifications].filter((n, i) => i < MAX_HISTORY || open.has(n.id))
      let peekIds = action.peek ? [action.notification.id, ...state.peekIds] : state.peekIds
      if (peekIds.length > MAX_PEEK) peekIds = peekIds.slice(0, MAX_PEEK)
      // #413 — a Suspicious toast has its own list, so a fifth Regular toast never pushes it out
      const kept = new Set(notifications.map((n) => n.id))
      const suspiciousIds = (action.notification.suspicious ? [action.notification.id, ...state.suspiciousIds] : state.suspiciousIds)
        .filter((id) => kept.has(id))
      return { ...state, notifications, peekIds, suspiciousIds, unreadCount: state.unreadCount + 1 }
    }
    case 'DISMISS_SUS':
      return { ...state, suspiciousIds: state.suspiciousIds.filter((id) => id !== action.id) }
    case 'CLOSE_SUS_EVENTS': {
      const closing = new Set(action.eventIds)
      const suspiciousIds = state.suspiciousIds.filter((id) => {
        const eventId = (state.notifications.find((n) => n.id === id)?.event as { id?: unknown } | null)?.id
        return !(typeof eventId === 'number' && closing.has(eventId))
      })
      return suspiciousIds.length === state.suspiciousIds.length ? state : { ...state, suspiciousIds }
    }
    case 'SIGN_OUT':
      return state.suspiciousIds.length === 0 ? state : { ...state, suspiciousIds: [] }
    case 'EXIT_PEEK':
      return {
        ...state,
        exitingPeekIds: state.exitingPeekIds.includes(action.id)
          ? state.exitingPeekIds
          : [...state.exitingPeekIds, action.id],
      }
    case 'DISMISS_PEEK':
      return {
        ...state,
        peekIds: state.peekIds.filter(id => id !== action.id),
        exitingPeekIds: state.exitingPeekIds.filter(id => id !== action.id),
      }
    case 'DISMISS_FROM_DRAWER': {
      const peekIds = state.peekIds.filter(id => id !== action.id)
      const exitingPeekIds = state.exitingPeekIds.filter(id => id !== action.id)
      const notifications = state.notifications.filter(n => n.id !== action.id)
      const removedWasUnread = state.notifications.find(n => n.id === action.id && !n.isRead)
      return {
        ...state,
        notifications,
        peekIds,
        suspiciousIds: state.suspiciousIds.filter(id => id !== action.id),
        exitingPeekIds,
        unreadCount: removedWasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      }
    }
    case 'TOGGLE_DRAWER': {
      const opening = !state.drawerOpen
      if (opening) {
        return {
          ...state,
          drawerOpen: true,
          unreadCount: 0,
          notifications: state.notifications.map(n => ({ ...n, isRead: true })),
        }
      }
      return { ...state, drawerOpen: false }
    }
    case 'MARK_ALL_READ':
      return {
        ...state,
        unreadCount: 0,
        notifications: state.notifications.map(n => ({ ...n, isRead: true })),
      }
    case 'CLEAR_ALL':
      return { ...state, notifications: [], peekIds: [], suspiciousIds: [], exitingPeekIds: [], unreadCount: 0 }
    case 'SET_TAB':
      return { ...state, activeTab: action.tab }
    default:
      return state
  }
}

// ── Provider ────────────────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    notifications: [],
    peekIds: [],
    suspiciousIds: [],
    exitingPeekIds: [],
    unreadCount: 0,
    drawerOpen: false,
    activeTab: 'all',
  })

  // ── Timer management ──
  // Map<id, { timeout, remaining, startedAt }>
  const timersRef = useRef<Map<string, { timeout: ReturnType<typeof setTimeout>; remaining: number; startedAt: number }>>(new Map())

  const dismissPeek = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (t) { clearTimeout(t.timeout); timersRef.current.delete(id) }
    dispatch({ type: 'DISMISS_PEEK', id })
  }, [])

  const startTimer = useCallback((id: string, duration: number) => {
    const existing = timersRef.current.get(id)
    if (existing) clearTimeout(existing.timeout)
    const timeout = setTimeout(() => {
      timersRef.current.delete(id)
      // Signal exit animation — PeekCard will call dismissPeek after animation completes
      dispatch({ type: 'EXIT_PEEK', id })
    }, duration)
    timersRef.current.set(id, { timeout, remaining: duration, startedAt: Date.now() })
  }, [])

  const pausePeekTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (!t) return
    clearTimeout(t.timeout)
    const elapsed = Date.now() - t.startedAt
    const remaining = Math.max(0, t.remaining - elapsed)
    timersRef.current.set(id, { timeout: t.timeout, remaining, startedAt: t.startedAt })
  }, [])

  const resumePeekTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (!t) return
    const duration = Math.max(HOVER_RESUME_DELAY, t.remaining)
    startTimer(id, duration)
  }, [startTimer])

  const addNotification = useCallback((event: any) => {
    const parsed = parseEvent(event)
    const detectedObjects = parsed.detectedObjects
    const variant: NotificationVariant = detectedObjects.length > 0 ? 'alert' : 'standard'
    const id = crypto.randomUUID()
    // #294 redesign — a critical event gets the critical popup (CriticalPopupStack) and goes into the list (the
    // bell's drawer). #337 (owner pick 2026-10-13, "Critical events also toast") — and a toast too, marked critical
    // (red, PeekCards) and with no timer: it stays until the user closes it (Clear all clears it) or the alert is
    // acknowledged (below). The cap is the same for every toast (MAX_PEEK). Closing the toast leaves the popup alone
    // (its own state).
    const critical = (event as { is_critical?: unknown } | null)?.is_critical === true
    // #413 — an open Suspicious event (not Critical, not already seen) gets the Suspicious toast instead: persistent,
    // no timer, its own list. It closes when anyone marks the event Seen (cache watcher below, suspicious_seen).
    const suspicious = !critical && isSuspiciousAlarm(event)
    const notification: DetectionNotification = {
      ...parsed,
      id,
      variant,
      ...(critical ? { critical: true } : {}),
      ...(suspicious ? { suspicious: true } : {}),
      isRead: false,
      createdAt: Date.now(),
    }
    dispatch({ type: 'ADD', notification, peek: !suspicious })
    if (critical || suspicious) return
    const duration = variant === 'alert' ? ALERT_DURATION : STANDARD_DURATION
    startTimer(id, duration)
  }, [startTimer])

  // #337 (fix round 1, review I-4) — a critical toast has no timer, but it does not outlive its alert: once its event
  // leaves the open-critical list (acknowledged anywhere: this tab, another tab, another dashboard; the WebSocket and
  // the poll refresh that list), the toast closes itself like a timed-out one, and the map pin it was holding stops
  // (#306). "Seen first": a toast can come before the list has its event (a poll or a replay), so it closes only
  // after its event was seen open. The list is only read from the cache (no request of its own). Closing a toast,
  // either way, never acknowledges anything.
  const queryClient = useQueryClient()
  const openPage = useSyncExternalStore(
    useCallback((onChange: () => void) => queryClient.getQueryCache().subscribe(onChange), [queryClient]),
    () => queryClient.getQueryData<PaginatedResponse<Event>>(queryKeys.events.criticalOpen()),
  )
  const seenOpenRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    for (const id of state.peekIds) {
      const n = state.notifications.find((x) => x.id === id)
      const eventId = (n?.event as { id?: unknown } | null)?.id
      if (!n?.critical || typeof eventId !== 'number' || state.exitingPeekIds.includes(id)) continue
      const open = stillOpen(openPage, eventId)
      if (open === true) seenOpenRef.current.add(eventId)
      else if (open === false && seenOpenRef.current.has(eventId)) dispatch({ type: 'EXIT_PEEK', id })
    }
  }, [openPage, state.peekIds, state.notifications, state.exitingPeekIds])

  // #413 — a Suspicious toast has no timer either; it closes when anyone presses Seen on its event. The cache watcher
  // (the same external-store read as the critical one above) sees this tab's optimistic Seen (rows, cards, modal,
  // toast) and any refetch after a colleague's Seen; the WebSocket's suspicious_seen closes them at once
  // (closeSuspiciousForEvents). Snapshot = the open toasts' events that a cached list shows as Seen, as a string ('' when
  // none: nothing to do). Accepted trade: a Seen that then fails has already closed the toast; the error toast shows and
  // the row stays open in the list.
  const openSusEventIds = state.suspiciousIds.flatMap((id) => {
    const eventId = (state.notifications.find((n) => n.id === id)?.event as { id?: unknown } | null)?.id
    return typeof eventId === 'number' ? [eventId] : []
  })
  const seenOpenSus = useSyncExternalStore(
    useCallback((onChange: () => void) => queryClient.getQueryCache().subscribe(onChange), [queryClient]),
    () => (openSusEventIds.length === 0 ? '' : seenEventIds(queryClient, openSusEventIds).join(',')),
  )
  useEffect(() => {
    if (seenOpenSus === '') return
    dispatch({ type: 'CLOSE_SUS_EVENTS', eventIds: seenOpenSus.split(',').map(Number) })
  }, [seenOpenSus])

  // #413 — signing out clears the Suspicious toasts at once (the next person on this browser sees none of them)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const wasAuthenticatedRef = useRef(isAuthenticated)
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) dispatch({ type: 'SIGN_OUT' })
    wasAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(t => clearTimeout(t.timeout))
      timersRef.current.clear()
    }
  }, [])

  const toggleDrawer = useCallback(() => dispatch({ type: 'TOGGLE_DRAWER' }), [])
  const markAllRead = useCallback(() => dispatch({ type: 'MARK_ALL_READ' }), [])
  const clearAll = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t.timeout))
    timersRef.current.clear()
    dispatch({ type: 'CLEAR_ALL' })
  }, [])
  const dismissFromDrawer = useCallback((id: string) => {
    const t = timersRef.current.get(id)
    if (t) { clearTimeout(t.timeout); timersRef.current.delete(id) }
    dispatch({ type: 'DISMISS_FROM_DRAWER', id })
  }, [])
  const dismissSuspicious = useCallback((id: string) => dispatch({ type: 'DISMISS_SUS', id }), [])
  const closeSuspiciousForEvents = useCallback((eventIds: number[]) => dispatch({ type: 'CLOSE_SUS_EVENTS', eventIds }), [])
  const setActiveTab = useCallback((tab: 'all' | 'alerts' | 'motion') => dispatch({ type: 'SET_TAB', tab }), [])

  const value: NotificationContextValue = {
    ...state,
    addNotification,
    dismissPeek,
    dismissSuspicious,
    closeSuspiciousForEvents,
    dismissFromDrawer,
    toggleDrawer,
    markAllRead,
    clearAll,
    setActiveTab,
    pausePeekTimer,
    resumePeekTimer,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <PeekCards />
      <NotificationDrawer />
    </NotificationContext.Provider>
  )
}
